import type { StateAction } from "@entrilia/shared/types";
import { isHtmlContentEmpty } from "@entrilia/shared/utilities/htmlHelper";
import type { InvestorCommunication } from "../../../../api/types/investorCommunicationsTypes";
import type {
  InvestorCommunicationEditState,
  InvestorCommunicationTypeEditModule,
} from "./investorCommunicationEditState";

export interface ArticleEditState {
  type: "Article";
  htmlBody: string;
  heroImageFile?: File;
  isHeroImageRemoved: boolean;
}

export interface ArticleValidationErrors {
  htmlBody?: string;
}

const bodyRequiredError = "Body is required";

const getSavedHtmlBody = (savedCommunication: InvestorCommunication): string =>
  savedCommunication.articleContent?.htmlBody ?? "";

const getInitialArticleEditState = (savedCommunication: InvestorCommunication): ArticleEditState => ({
  type: "Article",
  htmlBody: getSavedHtmlBody(savedCommunication),
  isHeroImageRemoved: false,
});

export const articleEditModule: InvestorCommunicationTypeEditModule<ArticleEditState> = {
  getInitialState: getInitialArticleEditState,
  isContentChanged: ({ htmlBody, heroImageFile, isHeroImageRemoved }, savedCommunication) =>
    htmlBody !== getSavedHtmlBody(savedCommunication) ||
    heroImageFile !== undefined ||
    (isHeroImageRemoved && savedCommunication.articleContent?.heroImageFileId !== undefined),
  isContentCompleted: ({ htmlBody }) => !isHtmlContentEmpty(htmlBody),
  validate: ({ htmlBody }, _savedCommunication, isCompletionRequired) => ({
    htmlBody: isCompletionRequired && isHtmlContentEmpty(htmlBody) ? bodyRequiredError : undefined,
  }),
  getContentUpdate: ({ htmlBody }, savedCommunication) =>
    htmlBody === getSavedHtmlBody(savedCommunication) ? undefined : { articleContent: { htmlBodyUserInput: htmlBody } },
  getPartiallySavedState: ({ heroImageFile, isHeroImageRemoved }, savedCommunication) => ({
    ...getInitialArticleEditState(savedCommunication),
    heroImageFile,
    isHeroImageRemoved,
  }),
};

const updateArticleState =
  (update: (state: ArticleEditState) => ArticleEditState): StateAction<InvestorCommunicationEditState> =>
  (state) =>
    state.typeState.type === "Article" ? { ...state, typeState: update(state.typeState) } : state;

export const setHtmlBodyAction = (htmlBody: string): StateAction<InvestorCommunicationEditState> =>
  updateArticleState((state) => ({ ...state, htmlBody }));

export const setHeroImageFileAction = (heroImageFile: File): StateAction<InvestorCommunicationEditState> =>
  updateArticleState((state) => ({ ...state, heroImageFile, isHeroImageRemoved: false }));

export const removeHeroImageAction = (): StateAction<InvestorCommunicationEditState> =>
  updateArticleState((state) => ({ ...state, heroImageFile: undefined, isHeroImageRemoved: true }));
